import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ComponentType, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { CellProps } from "react-table";

import {
  AdminLabOrderList,
  CaseAdmin,
  CaseDocumentAdmin,
  LabOrder,
  SlideAdmin,
} from "pwapi";

import { printLabOrdersSlides } from "shared/actions/admin/adminCaseActions";
import { printSlideAdminLabOrder } from "shared/actions/labOrdersActions";
import Menu from "shared/components/Menu";
import { useIsLoadingAny } from "shared/hooks/requestStatusHooks";
import {
  ExternalLabsState,
  ExternalLabStainsState,
} from "shared/reducers/frontLoaderReducers";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { timestampToLocaleDateTimeString } from "shared/utils/dates";
import { getDocumentLabel } from "shared/utils/documentUtils";
import { backendUrl } from "shared/utils/environment";
import { splitFlagReason } from "shared/utils/labOrderUtils";
import {
  printSendoutRequisitionLink,
  printSlidesLink,
} from "shared/utils/printUtils";
import { getStainByLabStainId } from "shared/utils/stainUtils";
import { getNameString } from "shared/utils/userUtils";

import ApplicationState from "store/ApplicationState";
import { useAppDispatch, useAppSelector } from "store/hooks";

import { useCopyBlockId } from "./CopyBlockButton";
import OrderCancellationButton from "./OrderCancellationButton";
import OrderCompletionButton from "./OrderCompletionButton";
import { getLabStainMeta } from "../../../hooks/useLabOrderMeta";
import LinkButton from "../../LinkButton";
import OrderIcon from "../../OrderIcon";
import { SlideFlagIcon } from "../../viewer/SlideFlagIcon";
import {
  getOrderStatus,
  mapOrderStatusToLabel,
} from "../editCasePanel/Orders.helpers";

const getOrderDocument = (
  documents: CaseDocumentAdmin[] | undefined,
  orderId: number | undefined
) =>
  Object.values(documents || {}).find(
    (document: any) =>
      document?.lab_order_id && orderId && document.lab_order_id === orderId
  ) as CaseDocumentAdmin | undefined;

export const withOrderStatusRowProp = (hooks: any) => {
  hooks.getRowProps.push((props: any, { row }: any) => {
    // row.original already contains your derived status
    // const status = row?.original?.status; // "completed" | "cancelled" | "in-progress"
    const order = row?.original;
    const status = getOrderStatus(order);

    return {
      ...props,
      ...(status ? { "data-order-status": status } : null),
    };
  });
};
export const withCellDataAttrs = (hooks: any) => {
  hooks.getCellProps.push((props: any, { cell }: any) => {
    const col = cell?.column?.id; // e.g., "status", "created_at"
    return {
      ...props,
      ...(col ? { "data-col": col } : null),
    };
  });
};

const OrdersColumns = <P extends object>(
  Component: ComponentType<P>,
  options?: {
    hasFlagReason?: boolean;
    canOpenCase?: boolean;
    canCancel?: boolean;
    canCompleteOrder?: boolean;
    displayClipboardButton?: boolean;
    printSlideFromOrderButton?: boolean;
  }
) => {
  const WithOrdersColumns = (props: any) => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const labStains = useAppSelector(({ labStains }) => labStains);
    const stains = useAppSelector(({ stains }) => stains);

    const isAdmin = useSelector(
      ({ auth }: ApplicationState) => auth.user?.is_superuser
    );
    const cancelSentOrderFlag = useAppSelector((state) =>
      selectFeatureFlag(state, "canCancelSentLabOrder")
    );
    const cancelAutomatedOrderFlag = useAppSelector((state) =>
      selectFeatureFlag(state, "canCancelAutomatedOrder")
    );
    const completeOrderFlag = useAppSelector((state) =>
      selectFeatureFlag(state, "canCompleteLabOrder")
    );
    const canPrintLabOrders = useAppSelector((state) =>
      selectFeatureFlag(state, "canPrintLabOrders")
    );
    const printSlideFromOrder = useAppSelector((state) =>
      selectFeatureFlag(state, "printSlideFromOrder")
    );

    const externalLabs = useAppSelector(
      ({ externalLabs }: { externalLabs: ExternalLabsState }) => externalLabs
    );
    const externalLabStains = useAppSelector(
      ({ externalLabStains }: { externalLabStains: ExternalLabStainsState }) =>
        externalLabStains
    );

    const getLabStainMetaForRow = useCallback(
      (labStainId: number | undefined) =>
        getLabStainMeta(labStainId, {
          stains,
          labStains,
          externalLabs,
          externalLabStains,
        }),
      [stains, labStains, externalLabs, externalLabStains]
    );

    const loadingPrintSlideAdminLabOrder = useIsLoadingAny(
      printSlideAdminLabOrder
    );
    const loadingPrintLabOrdersSlides = useIsLoadingAny(printLabOrdersSlides);

    const copyBlockId = useCopyBlockId();

    const columns = useMemo(
      () =>
        [
          {
            Header: "Status",
            accessor: "status",
            disableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) => {
              const orderStatus = getOrderStatus(row.original);
              return (
                <>
                  <OrderIcon status={orderStatus} />{" "}
                  {mapOrderStatusToLabel[orderStatus]}
                </>
              );
            },
          },

          ...(options?.canOpenCase
            ? [
                {
                  Header: "Case Priority",
                  accessor: "case_priority",
                  Cell: ({ row }: CellProps<AdminLabOrderList>) => {
                    const { case_priority: priority } = row.original;
                    return priority &&
                      priority !== CaseAdmin.priority.NORMAL ? (
                      <div
                        className={`AdminCaselist__priority AdminCaselist__priority--${priority}`}
                      >
                        {priority}
                      </div>
                    ) : row.original.clinic_tat ? (
                      <div
                        className={`AdminCaselist__priority AdminCaselist__priority--tat`}
                      >
                        Priority
                      </div>
                    ) : null;
                  },
                },
                {
                  Header: "Case Accession",
                  accessor: "case__accession",
                  Cell: ({ row }: CellProps<AdminLabOrderList>) => (
                    <>{row.original.case_accession}</>
                  ),
                },
              ]
            : []),
          {
            Header: "Type",
            accessor: "type",
            Cell: ({ row }: CellProps<AdminLabOrderList>) => {
              const stain = getStainByLabStainId(
                row.original.lab_stain_id || 0,
                stains,
                labStains
              );

              const orderDocument = getOrderDocument(
                props.documents,
                row.original.id
              );
              const hasUploadedDocumentForOrder =
                !!orderDocument &&
                orderDocument.status === CaseDocumentAdmin.status.UPLOADED;
              const hasPendingDocumentForOrder =
                !!orderDocument &&
                orderDocument.status === CaseDocumentAdmin.status.PENDING;

              const documentLabel = stain?.deliverable_document_category
                ? getDocumentLabel(
                    stain.deliverable_document_category as CaseDocumentAdmin.category
                  )
                : null;

              return (
                <>
                  {documentLabel && !row.original.cancelled_at ? (
                    hasUploadedDocumentForOrder ? (
                      <LinkButton
                        nocolor
                        className="PWTable__documentCategory"
                        onClick={() =>
                          props.setPreviewDocumentId?.(orderDocument?.id)
                        }
                      >
                        <FontAwesomeIcon
                          icon="file"
                          className="PWTable__documentCategory--margin OrderIcon--complete"
                        />
                        {documentLabel}
                      </LinkButton>
                    ) : (
                      <Link
                        className="PWTable__documentCategory"
                        to={`/admin/cases/${row.original.case_id}${
                          hasPendingDocumentForOrder
                            ? ""
                            : `/orders/${row.original.id}`
                        }/documents/${
                          hasPendingDocumentForOrder
                            ? orderDocument?.id
                            : "upload"
                        }`}
                      >
                        <FontAwesomeIcon
                          icon="file-upload"
                          className="PWTable__documentCategory--margin OrderIcon--in_progress"
                        />
                        {documentLabel}
                      </Link>
                    )
                  ) : (
                    <span>{row.original.type}</span>
                  )}
                </>
              );
            },
          },
          {
            accessor: "is_sendout",
            disableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) => {
              const { lab_stain_id } = row.original;
              const { isExternalLabStain } =
                getLabStainMetaForRow(lab_stain_id);

              return (
                isExternalLabStain && (
                  <span className="icon-wrap">
                    <FontAwesomeIcon
                      className="orders__sendout--icon"
                      icon="truck"
                      title="Sendout"
                    />
                  </span>
                )
              );
            },
          },

          {
            Header: "Ordered Stains",
            accessor: "stain_name",
            Cell: ({ row }: CellProps<AdminLabOrderList>) => {
              const stain = getStainByLabStainId(
                row.original.lab_stain_id || 0,
                stains,
                labStains
              );

              if (!stain) return null;

              const { lab_stain_id } = row.original;
              const { labStain, externalLabName } =
                getLabStainMetaForRow(lab_stain_id);

              return (
                <span
                  className={
                    row.original.cancelled_at ? "orders__cancelled" : ""
                  }
                >
                  {externalLabName && (
                    <span className={"orders__sendout--lab"}>
                      {externalLabName}
                    </span>
                  )}
                  <span>{row.original.block_name}</span>{" "}
                  <strong>{stain.name}</strong>{" "}
                  <span style={{ color: "var(--steel)" }}>
                    {stain.category}
                  </span>
                  {labStain?.label_override && <>: {labStain.label_override}</>}
                </span>
              );
            },
          },
          {
            Header: "Ordering User",
            accessor: "user",
            disableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) => (
              <span
                className={row.original.cancelled_at ? "orders__cancelled" : ""}
              >
                {getNameString(
                  row.original.user?.first_name,
                  row.original.user?.last_name,
                  true
                )}
              </span>
            ),
          },
          ...(options?.canOpenCase
            ? [
                {
                  Header: "Patient",
                  accessor: "patient",
                  disableSortBy: true,
                  Cell: ({ row }: CellProps<AdminLabOrderList>) => (
                    <>
                      {getNameString(
                        row.original.patient?.first_name,
                        row.original.patient?.last_name,
                        true
                      )}
                    </>
                  ),
                },
              ]
            : []),
          {
            Header: "Order Placed At",
            accessor: "created_at",
            Cell: ({ row }: CellProps<AdminLabOrderList>) => (
              <span
                className={row.original.cancelled_at ? "orders__cancelled" : ""}
              >
                {timestampToLocaleDateTimeString(row.original.created_at || "")}
              </span>
            ),
          },
          {
            Header: "Completed At",
            accessor: "completed_at",
            diableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) =>
              row.original.completed_at ? (
                <>
                  {timestampToLocaleDateTimeString(
                    row.original.completed_at || ""
                  )}
                </>
              ) : (
                <></>
              ),
          },
          ...(canPrintLabOrders && options?.canOpenCase
            ? [
                {
                  Header: "Printed At",
                  accessor: "printed_at",
                  disableSortBy: true,
                  Cell: ({ row }: CellProps<AdminLabOrderList>) => {
                    const printer = getNameString(
                      //@ts-ignore
                      row.original.printed_by?.first_name,
                      //@ts-ignore
                      row.original.printed_by?.last_name,
                      true
                    );
                    return row.original.printed_at ? (
                      <span>
                        {timestampToLocaleDateTimeString(
                          row.original.printed_at || ""
                        )}
                        {printer && (
                          <>
                            <br />
                            {printer}
                          </>
                        )}
                      </span>
                    ) : (
                      <></>
                    );
                  },
                },
              ]
            : []),
          {
            Header: "Note",
            accessor: "note",
            disableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) => {
              return (
                <div
                  style={{ maxWidth: "400px" }}
                  className={
                    row.original.cancelled_at ? "orders__cancelled" : ""
                  }
                >
                  {options?.hasFlagReason && row.original.flag?.reason && (
                    <>
                      <SlideFlagIcon />
                      <span style={{ color: "var(--barnRed)" }}>
                        {" "}
                        {splitFlagReason(row.original.flag.reason)}
                      </span>
                      <br />
                    </>
                  )}
                  {row.original.note}
                </div>
              );
            },
          },
          {
            Header: "Actions",
            disableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) => {
              const labStainId = row.original.lab_stain_id || 0;
              const labStain = labStains[labStainId];
              const stain = getStainByLabStainId(labStainId, stains, labStains);

              const externalLab =
                externalLabs[
                  externalLabStains[labStain?.external_lab_stain_id || 0]
                    ?.external_lab_id || 0
                ];
              const orderDocument = getOrderDocument(
                props.documents,
                row.original.id
              );
              const hasDocumentForOrder = !!orderDocument;
              const hasUploadedDocumentForOrder =
                !!orderDocument &&
                orderDocument.status === CaseDocumentAdmin.status.UPLOADED;

              const isLisPending =
                props.caseStatus === CaseAdmin.status.LIS_PENDING;

              const canPrintSlide =
                options?.printSlideFromOrderButton &&
                printSlideFromOrder &&
                props.ordersWithoutSlides
                  ?.map((o: LabOrder) => o.id)
                  .includes(row.original.id) &&
                !row.original.completed_at &&
                !row.original.cancelled_at;

              const canComplete =
                options?.canCompleteOrder &&
                completeOrderFlag &&
                !row.original.completed_at &&
                !row.original.cancelled_at;

              const canCancel =
                options?.canCancel &&
                ((row.original.user?.id && cancelSentOrderFlag) ||
                  (!row.original.user?.id && cancelAutomatedOrderFlag)) &&
                !row.original.completed_at &&
                !row.original.cancelled_at;
              const canUploadDocument =
                stain && stain.deliverable_document_category;
              const canPreviewDocument =
                hasUploadedDocumentForOrder && props.setPreviewDocumentId;
              const canSendoutRequisition = externalLab && row.original.case_id;
              const canCopyId =
                options?.displayClipboardButton && row.original.block_id;

              const showActions =
                canPrintSlide ||
                canComplete ||
                canCancel ||
                hasDocumentForOrder ||
                canUploadDocument ||
                canPreviewDocument ||
                canSendoutRequisition ||
                canCopyId ||
                options?.canOpenCase ||
                row.original.slide_id ||
                (row.original.id && isAdmin);

              if (!showActions) {
                return <></>;
              }

              return (
                <Menu>
                  <Menu.Trigger>
                    <button
                      type="button"
                      aria-label="Order actions"
                      title="Actions"
                    >
                      <FontAwesomeIcon icon="ellipsis-v" />
                    </button>
                  </Menu.Trigger>
                  <Menu.Items>
                    {hasDocumentForOrder ? (
                      <Menu.Item
                        icon="file-edit"
                        as={Link}
                        to={`/admin/cases/${row.original.case_id}/documents/${orderDocument?.id}`}
                        disabled={isLisPending}
                      >
                        Edit Document
                      </Menu.Item>
                    ) : (
                      canUploadDocument && (
                        <Menu.Item
                          as={Link}
                          icon="file-upload"
                          to={`/admin/cases/${row.original.case_id}/orders/${row.original.id}/documents/upload`}
                        >
                          Upload Document
                        </Menu.Item>
                      )
                    )}
                    {canPreviewDocument && (
                      <Menu.Item
                        icon="eye"
                        as="button"
                        onClick={() => {
                          props.setPreviewDocumentId(orderDocument?.id);
                        }}
                      >
                        Preview Document
                      </Menu.Item>
                    )}
                    {externalLab && row.original.case_id && (
                      <Menu.Item
                        as={Link}
                        icon="print"
                        to={printSendoutRequisitionLink(row.original.case_id)}
                        target="_blank"
                      >
                        Sendout Requisition
                      </Menu.Item>
                    )}
                    {canCopyId && (
                      <Menu.Item
                        icon="copy"
                        onClick={() => copyBlockId(row.original.block_id!)}
                      >
                        Copy block
                      </Menu.Item>
                    )}
                    {options?.canOpenCase && (
                      <Menu.Item
                        as={Link}
                        icon="pencil"
                        to={`/admin/cases/${row.original.case_id}`}
                        state={{
                          label: "Orders",
                          prevUrl: `${location.pathname}${location.search}`,
                        }}
                      >
                        Edit Case
                      </Menu.Item>
                    )}
                    {canPrintSlide && (
                      <Menu.Item
                        disabled={
                          loadingPrintSlideAdminLabOrder ||
                          loadingPrintLabOrdersSlides
                        }
                        onClick={() => {
                          dispatch(
                            printSlideAdminLabOrder({ id: row.original.id! })
                          ).then(
                            (r: SlideAdmin) =>
                              r.case?.id &&
                              r.id &&
                              window.open(
                                printSlidesLink(r.case.id, r, "zebra")
                              )
                          );
                        }}
                        icon="notes-medical"
                      >
                        Create Slide for Order
                      </Menu.Item>
                    )}
                    {canComplete && (
                      <OrderCompletionButton order={row.original as LabOrder} />
                    )}
                    {canCancel && (
                      <OrderCancellationButton
                        order={row.original as LabOrder}
                      />
                    )}
                    {row.original.slide_id && (
                      <Menu.Item
                        as={Link}
                        icon="flag"
                        to={`/cases/${row.original.case_id}/slides/${row.original.slide_id}`}
                        title="View Slide"
                        target="_blank"
                      >
                        View Slide
                      </Menu.Item>
                    )}
                    {row.original.id && isAdmin && (
                      <Menu.Item
                        as={Link}
                        icon="cogs"
                        to={`${backendUrl(false)}lab/laborder/${
                          row.original.id
                        }/change/`}
                        target="_blank"
                      >
                        View in Django
                      </Menu.Item>
                    )}
                  </Menu.Items>
                </Menu>
              );
            },
          },
        ] as any,
      [
        location,
        loadingPrintSlideAdminLabOrder,
        printSlideFromOrder,
        props.ordersWithoutSlides,
        props.documents,
        copyBlockId,
      ]
    );

    return <Component columns={columns} {...props} />;
  };

  // Set a display name for the HOC for easier debugging in React DevTools
  WithOrdersColumns.displayName = `OrdersColumns(${
    Component.displayName || Component.name || "Component"
  })`;

  return WithOrdersColumns;
};

export default OrdersColumns;
